import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useGetHelpQuery } from 'services/cms';
import theme from 'theme';
import { getReportEmailDeviceBlock } from 'utils/reportEmailDeviceInfo';
import { capitalize } from 'utils/string';

import Grid from 'components/Grid';
import ContactBlock, { ContactBlockConfig } from 'components/Help/ContactBlock';
import HelpPageLayout from 'components/Help/HelpPageLayout';
import IsLoading from 'components/isLoading';

type ContactUsConfig = { modes: ContactBlockConfig[]; socialMedia: ContactBlockConfig[] };
type SocialMediaItem = {
  socialMidiaName: string;
  SocialMidiaLink: string;
};

const ContactUsScreen: React.FC = () => {
  const { data, isLoading } = useGetHelpQuery();
  const contentSpacing = useBreakpointValueGS({ xl: 32, lg: 32, base: 16 }) || 16;
  const titleFontSize = useBreakpointValueGS({
    xl: theme.fontSizesPx['3xl'],
    lg: theme.fontSizesPx['3xl'],
    base: theme.fontSizesPx.lg,
  });
  const contentColumns = useBreakpointValueGS({ xl: 7, lg: 5, md: 4, sm: 2, base: 2 });
  const blockRenderer = (config: ContactBlockConfig) => <ContactBlock {...config} />;

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  if (!data) {
    // TODO: definir comportamento ao receber erros da API
    return null;
  }

  const socialTitleMap: Record<ContactBlockConfig['icon'], string> = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'X',
    bluesky: 'Bluesky',
    tiktok: 'Tiktok',
    youtube: 'Youtube',
    email: 'Email',
    informar_problema: 'Informar problema',
  };

  const normalizeSocialIcon = (value: string): ContactBlockConfig['icon'] | undefined => {
    const normalized = value.trim().toLowerCase();

    if (normalized === 'x') {
      return 'twitter';
    }

    return normalized as ContactBlockConfig['icon'];
  };

  const ReportEmail = 'faleconosco@bandplay.app';
  const reportEmailSubject = 'Informar problema no conteúdo do Bandplay';
  const reportEmailBody = `Descreva seu problema: \n\nConteúdo (título):\nLink (se houver):\n\n${getReportEmailDeviceBlock()}\n\nObrigado.`;
  const blocksConfig: ContactUsConfig = {
    modes: [
      {
        icon: 'email',
        description: 'Envie um e-mail',
        title: 'faleconosco@\nbandplay.app',
        link: `mailto:${ReportEmail}`,
      },
      {
        icon: 'informar_problema',
        description: 'Encontrou algum problema?\nFale conosco',
        title: 'Informar problema',
        link: `mailto:${ReportEmail}?subject=${encodeURIComponent(
          reportEmailSubject
        )}&body=${encodeURIComponent(reportEmailBody)}`,
      },
    ],
    socialMedia: data?.socialMidia.midiaItems.map((midia: SocialMediaItem) => {
      const icon = normalizeSocialIcon(midia.socialMidiaName);

      return {
        icon: icon as ContactBlockConfig['icon'],
        title: icon ? socialTitleMap[icon] ?? capitalize(icon) : capitalize(midia.socialMidiaName),
        link: midia.SocialMidiaLink,
      };
    }),
  };

  return (
    <HelpPageLayout>
      <View style={[styles.sectionContainer, { gap: contentSpacing }]}>
        <Text style={[styles.sectionTitle, { fontSize: titleFontSize }]}>
          {data?.contact.title}
        </Text>

        <Grid data={blocksConfig.modes} renderItem={blockRenderer} numColumns={contentColumns} />
      </View>

      <View style={[styles.sectionContainer, styles.socialSection, { gap: contentSpacing }]}>
        <Text style={[styles.sectionTitle, { fontSize: titleFontSize }]}>
          {data?.socialMidia.title}
        </Text>

        <Grid
          renderItem={blockRenderer}
          numColumns={contentColumns}
          data={blocksConfig.socialMedia}
        />
      </View>
    </HelpPageLayout>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionContainer: {
    paddingHorizontal: 16,
  },
  socialSection: {
    paddingVertical: 32,
  },
  sectionTitle: {
    color: theme.colors.base.white,
    fontWeight: 'bold',
    fontFamily: 'system-ui',
  },
});

export default ContactUsScreen;
